import React from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Image,
  Input,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Textarea,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { arrayUnion, collection, addDoc, doc, getFirestore, updateDoc } from 'firebase/firestore';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import {
  MdArticle,
  MdCloudUpload,
  MdClose,
  MdDelete,
  MdFolder,
  MdVerifiedUser,
  MdVisibility,
} from 'react-icons/md';
import Card from 'components/card/Card';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';
import {
  INSTRUCTOR_DOCUMENT_SECTIONS,
  buildInstructorDocumentEntry,
  formatInstructorDocumentDate,
  groupInstructorDocuments,
  normalizeInstructorDocuments,
} from 'utils/instructorDocuments';

const INSTRUCTOR_AGREEMENT_URL = 'https://firebasestorage.googleapis.com/v0/b/fitgroupusaapp-prod.appspot.com/o/T%26Cs%2Ftermsandconditions.pdf?alt=media&token=cfafd844-415f-44ed-91f1-d2d5aef6dee3&_gl=1*9tng8a*_ga*NjAzMDQ5NjkzLjE2NDI1MDMxNDQ.*_ga_CW55HF8NVT*MTY5ODQ5OTM4OC4xMjIuMS4xNjk4NDk5NDIwLjI4LjAuMA..';

const db = getFirestore(app);
const storage = getStorage(app);

export const isImageDocument = (document = {}) => {
  const safeDocument = document || {};
  const value = `${safeDocument.fileName || ''} ${safeDocument.URL || ''}`.toLowerCase();
  return /\.(png|jpe?g|gif|webp)(\?|$|\s)/.test(value);
};

function DocumentPreviewThumbnail({ document }) {
  const thumbBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  if (isImageDocument(document) && document.URL) {
    return (
      <Image
        src={document.URL}
        alt={document.fileName || 'Document preview'}
        w="48px"
        h="48px"
        borderRadius="12px"
        objectFit="cover"
        border="1px solid"
        borderColor={borderColor}
        flexShrink={0}
      />
    );
  }

  return (
    <Flex
      align="center"
      justify="center"
      w="48px"
      h="48px"
      borderRadius="12px"
      bg={thumbBg}
      color="brand.500"
      flexShrink={0}
    >
      <Icon as={MdArticle} w="24px" h="24px" />
    </Flex>
  );
}

function FullscreenDocumentViewer({ document, isOpen, onClose }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const viewerBg = useColorModeValue('white', 'gray.900');
  const headerBg = useColorModeValue('white', 'gray.800');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent bg={viewerBg}>
        <Flex
          align="center"
          justify="space-between"
          gap="14px"
          px={{ base: '18px', md: '28px' }}
          py="16px"
          borderBottom="1px solid"
          borderColor={borderColor}
          bg={headerBg}
        >
          <Flex align="center" gap="12px" minW="0">
            <DocumentPreviewThumbnail document={document} />
            <Box minW="0">
              <Text color={textColor} fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" noOfLines={1}>
                {document?.fileName || 'Instructor Agreement'}
              </Text>
              <Text color={mutedText} fontSize="sm">
                Document Viewer • {document?.Category || 'Agreement'} • {formatInstructorDocumentDate(document?.Date)}
              </Text>
            </Box>
          </Flex>
          <IconButton
            aria-label="Close document viewer"
            icon={<Icon as={MdClose} />}
            variant="ghost"
            size="lg"
            onClick={onClose}
          />
        </Flex>
        {document?.Notes && (
          <Box px={{ base: '18px', md: '28px' }} py="12px" borderBottom="1px solid" borderColor={borderColor}>
            <Text color={mutedText} fontSize="sm">
              {document.Notes}
            </Text>
          </Box>
        )}
        <Box flex="1" minH="0" overflow="auto" bg={viewerBg}>
          {isImageDocument(document) ? (
            <Flex align="center" justify="center" minH="calc(100vh - 88px)" p={{ base: '18px', md: '28px' }}>
              <Image
                src={document?.URL}
                alt={document?.fileName || 'Document preview'}
                maxH="calc(100vh - 124px)"
                maxW="100%"
                objectFit="contain"
                borderRadius="12px"
              />
            </Flex>
          ) : (
            <Box
              as="iframe"
              title={document?.fileName || 'Document preview'}
              src={document?.URL}
              w="100%"
              h="calc(100vh - 82px)"
              border="0"
            />
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
}

function DocumentRow({ document, onView, onDelete }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const rowBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  return (
    <Flex
      align={{ base: 'flex-start', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      justify="space-between"
      gap="14px"
      p="14px"
      borderRadius="12px"
      bg={rowBg}
    >
      <Flex align="center" gap="12px">
        <DocumentPreviewThumbnail document={document} />
        <Box>
          <Text color={textColor} fontWeight="700">
            {document.fileName || 'Uploaded document'}
          </Text>
          <Text color={mutedText} fontSize="sm">
            {formatInstructorDocumentDate(document.Date)}
          </Text>
          {document.Notes && (
            <Text color={mutedText} fontSize="sm" mt="4px">
              {document.Notes}
            </Text>
          )}
        </Box>
      </Flex>
      <Flex gap="8px">
        <Button
          size="sm"
          colorScheme="brand"
          variant="outline"
          leftIcon={<Icon as={MdVisibility} />}
          onClick={() => onView(document)}
        >
          View
        </Button>
        <IconButton
          aria-label={`Delete ${document.fileName || 'document'}`}
          size="sm"
          colorScheme="red"
          variant="outline"
          icon={<Icon as={MdDelete} />}
          onClick={() => onDelete(document)}
        />
      </Flex>
    </Flex>
  );
}

function DocumentSection({ title, documents, onView, onDelete }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');

  return (
    <Card p="20px">
      <Flex justify="space-between" align="center" mb="16px">
        <Flex align="center" gap="10px">
          <Icon as={MdFolder} color="brand.500" w="22px" h="22px" />
          <Text color={textColor} fontSize="lg" fontWeight="800">
            {title}
          </Text>
        </Flex>
        <Badge colorScheme="blue" borderRadius="8px" px="10px">
          {documents.length}
        </Badge>
      </Flex>
      <Flex direction="column" gap="12px">
        {documents.length > 0 ? (
          documents.map((document, index) => (
            <DocumentRow
              key={`${document.URL || document.fileName || title}-${index}`}
              document={document}
              onView={onView}
              onDelete={onDelete}
            />
          ))
        ) : (
          <Text color={mutedText} fontSize="sm">
            No documents uploaded yet.
          </Text>
        )}
      </Flex>
    </Card>
  );
}

export default function InstructorDocuments() {
  const { user, userDocId, userData, refreshUserData } = React.useContext(AuthContext);
  const documents = normalizeInstructorDocuments(userData?.InstructorDocuments);
  const groupedDocuments = groupInstructorDocuments(documents);
  const uploadDisclosure = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const viewerDisclosure = useDisclosure();
  const toast = useToast();
  const fileInputRef = React.useRef(null);

  const [category, setCategory] = React.useState('Insurance');
  const [file, setFile] = React.useState(null);
  const [notes, setNotes] = React.useState('');
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [isUploading, setIsUploading] = React.useState(false);
  const [activeDocument, setActiveDocument] = React.useState(null);
  const [documentToDelete, setDocumentToDelete] = React.useState(null);
  const cancelDeleteRef = React.useRef(null);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const iconBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const statBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const resetForm = () => {
    setCategory('Insurance');
    setFile(null);
    setNotes('');
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const logDocumentActivity = async ({ title, body, type }) => {
    if (!userDocId) return;

    await addDoc(collection(db, 'Users', userDocId, 'Activity'), {
      title,
      body,
      type,
      from: user?.email || userData?.Email || 'instructor',
      timestamp: new Date(),
    });
  };

  const openViewer = async (document) => {
    setActiveDocument(document);
    viewerDisclosure.onOpen();
    try {
      await logDocumentActivity({
        title: `Document Viewed: ${document.fileName || 'Instructor Agreement'}`,
        body: `${user?.email || userData?.Email || 'Instructor'} viewed ${document.fileName || 'Instructor Agreement'}.`,
        type: 'document_view',
      });
    } catch (error) {
      console.error('Error logging document view activity:', error);
    }
  };

  const openDeleteConfirmation = (document) => {
    setDocumentToDelete(document);
    deleteDisclosure.onOpen();
  };

  const handleDeleteDocument = async () => {
    if (!userDocId || !documentToDelete) return;

    try {
      const nextDocuments = documents.filter((_, index) => index !== documentToDelete._documentIndex);

      await updateDoc(doc(db, 'Users', userDocId), {
        InstructorDocuments: nextDocuments,
      });

      await logDocumentActivity({
        title: `Document Deleted: ${documentToDelete.fileName || 'Uploaded document'}`,
        body: `${user?.email || userData?.Email || 'Instructor'} deleted ${documentToDelete.fileName || 'Uploaded document'}.`,
        type: 'document_delete',
      });

      if (activeDocument?._documentIndex === documentToDelete._documentIndex) {
        setActiveDocument(null);
        viewerDisclosure.onClose();
      }

      await refreshUserData?.();
      toast({
        title: 'Document deleted',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      deleteDisclosure.onClose();
      setDocumentToDelete(null);
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleUpload = async () => {
    if (!userDocId || !file) {
      toast({
        title: 'Choose a file first',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);

    try {
      const timestamp = Date.now();
      const storagePath = `instructor-uploads/${userDocId}/${timestamp}_${file.name}`;
      const uploadTask = uploadBytesResumable(ref(storage, storagePath), file);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const nextProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(Math.round(nextProgress));
          },
          reject,
          resolve
        );
      });

      const url = await getDownloadURL(uploadTask.snapshot.ref);
      const entry = buildInstructorDocumentEntry({
        url,
        category,
        fileName: file.name,
        notes,
        uploadedBy: user?.email || userData?.Email,
        date: timestamp,
      });

      await updateDoc(doc(db, 'Users', userDocId), {
        InstructorDocuments: arrayUnion(entry),
      });

      await logDocumentActivity({
        title: `Document Uploaded: ${file.name}`,
        body: `Type: ${category}${notes ? `\nNotes: ${notes}` : ''}`,
        type: 'document_upload',
      });

      await refreshUserData?.();
      toast({
        title: 'Document uploaded',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      resetForm();
      uploadDisclosure.onClose();
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card id="documents-upload-widget" p="24px" mb="20px">
        <Flex
          align={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          justify="flex-end"
          gap="16px"
          mb="22px"
        >
          <Flex gap="10px" wrap="wrap">
            <Button leftIcon={<Icon as={MdCloudUpload} />} colorScheme="brand" onClick={uploadDisclosure.onOpen}>
              Upload to FGUSA
            </Button>
          </Flex>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 3 }} gap="16px">
          <Box bg={statBg} borderRadius="14px" p="18px">
            <Stat>
              <StatLabel color={mutedText}>Total Documents</StatLabel>
              <StatNumber color={textColor}>{documents.length}</StatNumber>
            </Stat>
          </Box>
          <Box bg={statBg} borderRadius="14px" p="18px">
            <Stat>
              <StatLabel color={mutedText}>Documents Viewed</StatLabel>
              <StatNumber color={textColor}>0</StatNumber>
            </Stat>
          </Box>
          <Box bg={statBg} borderRadius="14px" p="18px">
            <Stat>
              <StatLabel color={mutedText}>Required Documents</StatLabel>
              <StatNumber color={textColor}>{INSTRUCTOR_DOCUMENT_SECTIONS.length}</StatNumber>
            </Stat>
          </Box>
        </SimpleGrid>
      </Card>

      <Card p="20px" mb="20px">
        <Flex
          align={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          gap="16px"
        >
          <Flex align="center" gap="12px">
            <Flex w="46px" h="46px" borderRadius="12px" align="center" justify="center" bg={iconBg}>
              <Icon as={MdVerifiedUser} color="brand.500" w="24px" h="24px" />
            </Flex>
            <Box>
              <Text color={textColor} fontWeight="800">
                Instructor Agreement
              </Text>
              <Text color={mutedText} fontSize="sm">
                Instructor Agreement Date: {formatInstructorDocumentDate(userData?.instructorAgreementDate)}
              </Text>
            </Box>
          </Flex>
          <Button
            colorScheme="brand"
            variant="outline"
            leftIcon={<Icon as={MdVisibility} />}
            onClick={() => openViewer({
              URL: INSTRUCTOR_AGREEMENT_URL,
              fileName: 'Instructor Agreement',
              Category: 'Agreement',
              Date: userData?.instructorAgreementDate,
            })}
          >
            View Agreement
          </Button>
        </Flex>
      </Card>

      <SimpleGrid id="documents-sections-widget" columns={{ base: 1, xl: 3 }} gap="20px">
        {INSTRUCTOR_DOCUMENT_SECTIONS.map((section) => (
          <DocumentSection
            key={section}
            title={section}
            documents={groupedDocuments[section] || []}
            onView={openViewer}
            onDelete={openDeleteConfirmation}
          />
        ))}
      </SimpleGrid>

      <FullscreenDocumentViewer
        document={activeDocument}
        isOpen={viewerDisclosure.isOpen}
        onClose={viewerDisclosure.onClose}
      />

      <Modal isOpen={uploadDisclosure.isOpen} onClose={isUploading ? undefined : uploadDisclosure.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex align="center" gap="10px">
              <Icon as={MdCloudUpload} color="brand.500" />
              Upload to FGUSA
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap="16px">
              <Flex
                align="center"
                justify="center"
                direction="column"
                gap="10px"
                minH="150px"
                border="1px dashed"
                borderColor="brand.300"
                borderRadius="16px"
                bg="blue.50"
                p="20px"
              >
                <Icon as={MdCloudUpload} color="brand.500" w="42px" h="42px" />
                <Text color={textColor} fontWeight="800">
                  Choose an instructor document
                </Text>
                <Input
                  ref={fileInputRef}
                  type="file"
                  display="none"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                />
                <Button
                  colorScheme="brand"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose File
                </Button>
                <Text color={mutedText} fontSize="sm">
                  {file ? file.name : 'No file selected'}
                </Text>
              </Flex>
              <FormControl>
                <FormLabel>Type</FormLabel>
                <Select value={category} onChange={(event) => setCategory(event.target.value)}>
                  {INSTRUCTOR_DOCUMENT_SECTIONS.map((section) => (
                    <option key={section} value={section}>
                      {section}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Optional notes"
                />
              </FormControl>
              {isUploading && <Progress value={uploadProgress} colorScheme="blue" borderRadius="8px" />}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={uploadDisclosure.onClose} isDisabled={isUploading}>
              Cancel
            </Button>
            <Button colorScheme="brand" onClick={handleUpload} isLoading={isUploading}>
              Upload
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={deleteDisclosure.isOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={deleteDisclosure.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="800">
              Delete Document?
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text color={mutedText} fontSize="sm">
                This removes {documentToDelete?.fileName || 'this document'} from your instructor profile documents.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={deleteDisclosure.onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" ml={3} onClick={handleDeleteDocument}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
